import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import {
  type ChannelLocationStatusDto,
  type DriversResponseDto,
  type ReputationAnalyticsDto,
  ReputationApi,
  type ReputationOverviewDto,
} from '@up2stay/up2promo-api-client';

import { useLocale } from '@/core/hooks/use-locale';
import { reputationQueryKeys } from '@/core/query-keys/reputation-query-keys';

type Scope = {
  /** Single location, or undefined for the whole workspace. */
  locationId?: string;
};

/** Adds an optional date window (ISO strings). */
type WindowedScope = Scope & {
  from?: string;
  to?: string;
};

const STALE_TIME = 60 * 1000;

/** Workspace (or single-location) reputation headline metrics + KPI series. */
export function useReputationOverview(
  workspaceId: string | undefined,
  scope: WindowedScope = {},
) {
  // Competitor teaser names are localized server-side (DEV-452).
  const locale = useLocale();
  return useQuery({
    queryKey: reputationQueryKeys.overview(
      workspaceId,
      scope.locationId ?? null,
      scope.from ?? null,
      scope.to ?? null,
      locale,
    ),
    queryFn: async () => {
      const query: Record<string, string> = {};
      if (scope.locationId) query.locationId = scope.locationId;
      if (scope.from) query.from = scope.from;
      if (scope.to) query.to = scope.to;
      const res = await ReputationApi.getOverview({
        path: { workspaceId: workspaceId as string },
        query,
      });
      return res.data as ReputationOverviewDto | undefined;
    },
    enabled: !!workspaceId,
    staleTime: STALE_TIME,
  });
}

/** Ranked positive/negative aspect drivers with sample review ids for drilldown. */
export function useReputationDrivers(
  workspaceId: string | undefined,
  scope: Scope = {},
) {
  return useQuery({
    queryKey: reputationQueryKeys.drivers(
      workspaceId,
      scope.locationId ?? null,
    ),
    queryFn: async () => {
      const res = await ReputationApi.getDrivers({
        path: { workspaceId: workspaceId as string },
        query: scope.locationId ? { locationId: scope.locationId } : {},
      });
      return res.data as DriversResponseDto | undefined;
    },
    enabled: !!workspaceId,
    staleTime: STALE_TIME,
  });
}

/** Full reputation analysis: metrics, trend, aspect mix, topics, benchmark. */
export function useReputationAnalytics(
  workspaceId: string | undefined,
  scope: WindowedScope = {},
) {
  return useQuery({
    queryKey: reputationQueryKeys.analytics(
      workspaceId,
      scope.locationId ?? null,
      scope.from ?? null,
      scope.to ?? null,
    ),
    queryFn: async () => {
      const query: Record<string, string> = {};
      if (scope.locationId) query.locationId = scope.locationId;
      if (scope.from) query.from = scope.from;
      if (scope.to) query.to = scope.to;
      const res = await ReputationApi.getAnalytics({
        path: { workspaceId: workspaceId as string },
        query,
      });
      return res.data as ReputationAnalyticsDto | undefined;
    },
    enabled: !!workspaceId,
    staleTime: STALE_TIME,
  });
}

/** Provider link status (GBP) for the workspace's locations. */
export function useChannelLocations(
  workspaceId: string | undefined,
  scope: Scope = {},
  options?: Pick<
    UseQueryOptions<ChannelLocationStatusDto[]>,
    'refetchInterval' | 'enabled'
  >,
) {
  return useQuery({
    queryKey: reputationQueryKeys.channelLocations(
      workspaceId,
      scope.locationId ?? null,
    ),
    queryFn: async () => {
      const res = await ReputationApi.getChannelLocations({
        path: { workspaceId: workspaceId as string },
        query: scope.locationId ? { locationId: scope.locationId } : {},
      });
      return res.data?.channelLocations ?? [];
    },
    enabled: Boolean(workspaceId) && (options?.enabled ?? true),
    staleTime: STALE_TIME,
    refetchInterval: options?.refetchInterval,
  });
}

/** Industry presets for location business-type selection. */
export function useReputationPresets(workspaceId: string | undefined) {
  return useQuery({
    queryKey: reputationQueryKeys.presets(workspaceId),
    queryFn: async () => {
      const res = await ReputationApi.getPresets({
        path: { workspaceId: workspaceId as string },
      } as never);
      return res.data?.presets ?? [];
    },
    enabled: !!workspaceId,
    staleTime: 5 * 60 * 1000,
  });
}

/** AI-generated executive summary for the reputation dashboard. */
export function useReputationSummary(
  workspaceId: string | undefined,
  scope: Scope = {},
  enabled = true,
) {
  return useQuery({
    queryKey: reputationQueryKeys.summary(
      workspaceId,
      scope.locationId ?? null,
    ),
    queryFn: async () => {
      const res = await ReputationApi.getSummary({
        path: { workspaceId: workspaceId as string },
        query: scope.locationId ? { locationId: scope.locationId } : {},
      });
      return res.data;
    },
    enabled: !!workspaceId && enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.status === 'generating' ? 2500 : false,
  });
}
